import { AlertTriangle, BadgeInfo } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Container, Sip } from '@sbclite/core';
import { useEffectiveSipPeerPolicy } from '../../../../hooks/useSipProfiles';
import { TypeBadge } from '../../../common';
import { DependencyChecklistCard } from '../../../common/data';
import { buildSipPeerPreflightModel } from '../../../../lib/preflight-checklist';

const provenanceLabels: Record<Sip.SipProfileSectionSource, { label: string; variant: 'blue' | 'green' | 'gray' }> = {
  'peer-profile': { label: 'peer-profile', variant: 'blue' },
  'default-profile': { label: 'default-profile', variant: 'green' },
  'legacy-peer-columns': { label: 'legacy-peer-columns', variant: 'gray' },
};

const stateLabels: Record<Sip.SipPeerPolicyReferenceState | 'ready' | 'warning', { label: string; variant: 'blue' | 'green' | 'gray' | 'yellow' | 'red' }> = {
  ready: { label: 'ready', variant: 'green' },
  warning: { label: 'warning', variant: 'yellow' },
  missing: { label: 'missing', variant: 'red' },
  disabled: { label: 'disabled', variant: 'yellow' },
  error: { label: 'error', variant: 'red' },
  optional: { label: 'optional', variant: 'gray' },
};

function ProvenanceBadge({ provenance }: { readonly provenance: Sip.SipProfileSectionSource }) {
  const config = provenanceLabels[provenance];
  return <TypeBadge label={config.label} variant={config.variant} size="sm" />;
}

function StateBadge({ state }: { readonly state: Sip.SipPeerPolicyReferenceState | 'warning' }) {
  const config = stateLabels[state];
  return <TypeBadge label={config.label} variant={config.variant} size="sm" />;
}

function SectionCard({
  title,
  section,
}: {
  readonly title: string;
  readonly section: Sip.SipProfileSectionResolution<unknown> | null;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-gray-900">{title}</div>
        {section ? <ProvenanceBadge provenance={section.provenance} /> : <TypeBadge label="missing" variant="gray" size="sm" />}
      </div>
      {section ? (
        <pre className="overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700">{JSON.stringify(section.value, null, 2)}</pre>
      ) : (
        <div className="text-sm text-gray-500">No resolved value.</div>
      )}
    </div>
  );
}

function ReferenceCard({
  title,
  reference,
  link,
}: {
  readonly title: string;
  readonly reference: Sip.SipPeerPolicyReference<unknown>;
  readonly link: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-gray-900">{title}</div>
        <StateBadge state={reference.state} />
      </div>
      <div className="text-sm text-gray-700">{reference.summary}</div>
      {reference.details.length > 0 && <div className="text-xs text-gray-500">{reference.details.join(' ')}</div>}
      <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
        <div>
          {reference.name ? <span className="font-medium text-gray-900">{reference.name}</span> : <span>Unresolved</span>}
        </div>
        <Link to={link} className="font-medium text-blue-600 hover:text-blue-800">
          Open
        </Link>
      </div>
    </div>
  );
}

function PlacementStateBadge({ candidate }: { readonly candidate: Container.EnginePlacementCandidate }) {
  const state = candidate.runtime.runtimeReadyState === 'ready'
    ? 'ready'
    : candidate.runtime.runtimeReadyState === 'not-ready'
      ? 'error'
      : candidate.peerFitReady === false || candidate.configurationReady === false
        ? 'warning'
        : 'warning';

  return <StateBadge state={state} />;
}

function PlacementCandidateCard({ candidate }: { readonly candidate: Container.EnginePlacementCandidate }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-gray-900">{candidate.container.containerId}</div>
        <PlacementStateBadge candidate={candidate} />
      </div>
      <div className="text-xs text-gray-500">
        Registry status {candidate.runtime.observedStatus}; runtime {candidate.runtime.runtimeReadyState}; observed at {candidate.runtime.observedAt}.
      </div>
      <div className="grid gap-2 text-sm md:grid-cols-2">
        <div className="rounded-md bg-gray-50 p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">Configuration</div>
          <div className="font-medium text-gray-900">{candidate.configurationReady ? 'Ready' : 'Not ready'}</div>
        </div>
        <div className="rounded-md bg-gray-50 p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">Runtime</div>
          <div className="font-medium text-gray-900">{candidate.runtime.runtimeReadyState}</div>
        </div>
        <div className="rounded-md bg-gray-50 p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">Peer fit</div>
          <div className="font-medium text-gray-900">{candidate.peerFitReady === null ? 'Unknown' : candidate.peerFitReady ? 'Ready' : 'Not ready'}</div>
        </div>
      </div>
      <div className="text-sm text-gray-700">
        {candidate.reasons.length > 0 ? candidate.reasons.join(' ') : 'No blocking reasons.'}
      </div>
      <div className="text-xs text-gray-500">Applied revision: unknown — no desired/applied tracking exists.</div>
    </div>
  );
}

export function EffectivePolicyTab({ peerId, containerId }: { readonly peerId: string | undefined; readonly containerId?: string }) {
  const policyQuery = useEffectiveSipPeerPolicy(peerId, containerId);

  if (!peerId) {
    return <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">Save the peer first to inspect effective policy.</div>;
  }

  if (policyQuery.isLoading) {
    return <div className="text-sm text-gray-600">Loading effective policy…</div>;
  }

  if (policyQuery.error) {
    return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">Failed to load effective policy.</div>;
  }

  const view = policyQuery.data?.data;
  if (!view) {
    return <div className="text-sm text-gray-600">No policy data.</div>;
  }

  const checklistModel = buildSipPeerPreflightModel(view);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <BadgeInfo className="mt-0.5 h-5 w-5 text-blue-600" />
        <div className="space-y-1 text-sm text-gray-700">
          <div className="font-medium text-gray-900">Effective policy</div>
          <div>Resolved backend snapshot for this peer with provenance, attachment state, and routing source.</div>
          {!view.policy.available && (
            <div className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-4 w-4" />
              Missing: {view.policy.report.missingSections.join(', ')}
            </div>
          )}
        </div>
      </div>

      {view.policy.profile && <div className="text-sm text-gray-600">Source profile: <span className="font-medium text-gray-900">{view.policy.profile.name}</span> ({view.policy.profile.source})</div>}

      <DependencyChecklistCard model={checklistModel} title="Operational preflight" subtitle="Backend-resolved peer prerequisites and effective policy." />

      <div className="grid gap-4 md:grid-cols-2">
        <ReferenceCard title="Peer SIP identity" reference={view.peerHost} link="/foundation/hosts" />
        <ReferenceCard title="SIP interface" reference={view.sipInterface} link="/foundation/sip-interfaces" />
        <ReferenceCard title="Interface SIP identity" reference={view.interfaceHost} link="/foundation/hosts" />
        <ReferenceCard title="Media realm" reference={view.mediaRealm} link="/foundation/media-realms" />
        <ReferenceCard title="Codec group" reference={view.codecGroup} link="/connectivity/sip-peers" />
        <ReferenceCard title="Peer TLS context" reference={view.peerTlsContext} link="/foundation/tls-contexts" />
        <ReferenceCard title="Interface TLS context" reference={view.interfaceTlsContext} link="/foundation/tls-contexts" />
        <ReferenceCard title="Interface manipulation" reference={view.interfaceManipulation} link="/connectivity/sip-manipulations" />
        <ReferenceCard title="Peer inbound manipulation" reference={view.peerInboundManipulation} link="/connectivity/sip-manipulations" />
        <ReferenceCard title="Peer outbound manipulation" reference={view.peerOutboundManipulation} link="/connectivity/sip-manipulations" />
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="font-medium text-gray-900">Routing source</div>
          <StateBadge state={view.routing.settings ? 'ready' : 'missing'} />
        </div>
        <div className="text-sm text-gray-700">{view.routing.summary}</div>
        <div className="text-xs text-gray-500">{view.routing.allRules.length} total rule(s); {view.routing.enabledRules.length} enabled.</div>
        <div className="grid gap-2 md:grid-cols-2">
          <div className="rounded-md bg-gray-50 p-3 text-sm">
            <div className="text-xs uppercase tracking-wide text-gray-500">Classification mode</div>
            <div className="font-medium text-gray-900">{view.routing.classificationMode ?? 'Missing'}</div>
          </div>
          <div className="rounded-md bg-gray-50 p-3 text-sm">
            <div className="text-xs uppercase tracking-wide text-gray-500">Rule precedence</div>
            <div className="font-medium text-gray-900">Priority-ordered, enabled-first snapshot</div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="font-medium text-gray-900">Engine placement</div>
          <StateBadge
            state={view.placement.selectionRequired
              ? 'missing'
              : view.placement.selectedContainer?.configurationReady && view.placement.selectedContainer.runtime.runtimeReadyState === 'ready'
                ? 'ready'
                : 'warning'}
          />
        </div>
        <div className="text-sm text-gray-700">{view.placement.summary}</div>
        <div className="text-xs text-gray-500">Applied revision: unknown — no applied/desired revision tracking exists in the database model.</div>
        {view.placement.selectedContainer ? (
          <PlacementCandidateCard candidate={view.placement.selectedContainer} />
        ) : (
          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-600">
            No container selected yet. Choose one of the candidate engines below.
          </div>
        )}
        <div className="grid gap-3 md:grid-cols-2">
          {view.placement.candidates.map((candidate) => (
            <PlacementCandidateCard key={candidate.container.containerId} candidate={candidate} />
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <SectionCard title="Session timers" section={view.policy.sessionTimers} />
        <SectionCard title="Registrar" section={view.policy.registrar} />
        <SectionCard title="Digest" section={view.policy.digest} />
        <SectionCard title="Media" section={view.policy.media} />
        <SectionCard title="Crypto suites" section={view.policy.cryptoSuites} />
      </div>
    </div>
  );
}
